import Box from "@mui/material/Box";
import { AxiosError, AxiosResponse } from "axios";
import { useMemo, useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useStateOptions } from "src/hooks/useStateOptions";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
import { Button } from "src/components/common/FormComponents";
import HomeTableSkeleton from "src/components/common/HomeTableSkeleton";
import HorizontalTabs from "src/components/common/HorizontalTabs";
import MultiSelect from "src/components/common/MultiSelect";
import SingleSelectFilter from "src/components/common/SingleSelectFilter";
import SearchBar from "src/components/common/SearchField";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";
import { useLandOwnershipEntityOptions } from "src/hooks/useLandOwnershipEntityOptions";
import {
  ExportStatusMessages2,
  ProjectType,
  registartionStatus,
  registeredEntitiesTabs,
  registeredLandTabs,
  statusMessages2,
} from "src/constants/common";
import { tableColumns } from "src/constants/tableColumns";
import {
  getAllProjectsData,
  refetchAllProjects,
} from "src/redux/slices/dashboard/projects";
import {
  getAllProjects,
  getAllRegistration,
  getProjectById,
} from "src/utils/api/dashboard";
import {
  dateAndTimeStamp,
  downloadS3File,
  downloadSheet,
  formatINR,
  getBatchCutoffDate,
  generateExportFilename,
} from "src/utils/helper";
import {
  RenderButton,
  RenderChip,
  RenderDemandDraft,
  RenderNewKml,
  RenderView,
  RenderViewDemandDraft,
} from "./RenderComponent";
import RenderTable from "./RenderTable";
import { path } from "src/constants/path";
type filterProjectType = ProjectType.TREE_PLANTATION | ProjectType.LAND_LEASED;
type RegistrationType = "registeredEntity" | "registeredLands";
const Mapping: Record<
  | "BUTTON"
  | "CHIP"
  | "DEMAND_DRAFT"
  | "VIEW_DEMAND"
  | "VIEW_BUTTON"
  | "NEW_KML",
  (params: any) => JSX.Element
> = {
  BUTTON: RenderButton,
  CHIP: RenderChip,
  DEMAND_DRAFT: RenderDemandDraft,
  VIEW_DEMAND: RenderViewDemandDraft,
  VIEW_BUTTON: RenderView,
  NEW_KML: RenderNewKml,
};
const Home = ({
  registrationType = "registeredEntity",
}: {
  registrationType?: RegistrationType;
}) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const PAGE_KEY =
    registrationType === "registeredEntity" ? "home_entities" : "home_lands";
  const { persisted, saveFilters } = usePersistedFilters(PAGE_KEY);

  const [projectData, setProjectData] = useState<any>({
    count: 0,
    rows: [],
  });
  const { states } = useStateOptions();
  const [searchParams] = useSearchParams();

  const tab = searchParams.get("currentTab");

  const [entityActiveTab, setEntityActiveTab] = useState(
    tab ?? persisted?.activeTab ?? undefined,
  );
  const [activeTab, setActiveTab] = useState(
    tab ?? persisted?.activeTab ?? undefined,
  );
  const [plantationBlockData, setPlantationBlockData] = useState<any>({
    count: 0,
    rows: [],
  });

  const [queryParams, setQueryParams] = useState<{
    status: any;
    offset: number;
    limit: number;
    sortBy: string;
    searchText: string;
    projectStateIds?: string[];
    download: boolean;
    entityIds?: string[];
  }>({
    status:
      registrationType === "registeredEntity" ? activeTab : entityActiveTab,
    offset: persisted?.queryParams?.offset ?? 0,
    limit: 50,
    sortBy: persisted?.queryParams?.sortBy ?? "updated_at:DESC",
    searchText: persisted?.queryParams?.searchText ?? "",
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    download: false,
    entityIds: persisted?.queryParams?.entityIds ?? undefined,
  });

  const [kmlCounts, setKmlCounts] = useState({
    notUploaded: 0,
    uploaded: 0,
    underQuery: 0,
    approved: 0,
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? { projectStateIds: null },
  );

  const [entityfilterKey, entitySetFilterKey] = useState(
    persisted?.entityFilterKey ?? { enetityStateIds: null },
  );

  useEffect(() => {
    const { download, ...qp } = queryParams;
    saveFilters({
      queryParams: qp,
      filterKey,
      entityFilterKey: entityfilterKey,
      activeTab:
        registrationType === "registeredEntity" ? entityActiveTab : activeTab,
    });
  }, [queryParams, filterKey, entityfilterKey, entityActiveTab, activeTab]);

  useQuery(
    ["kmlStatusCounts"],
    () => getAllRegistration({ limit: 9999, offset: 0, sortBy: "updated_at:DESC", download: false }),
    {
      onSuccess: (data) => {
        const rows: any[] = data?.data?.rows ?? [];
        setKmlCounts({
          notUploaded: rows.filter((r) => !r.newKmlStatus).length,
          uploaded: rows.filter((r) => r.newKmlStatus === "PENDING_REVIEW").length,
          underQuery: rows.filter((r) => r.newKmlStatus === "UNDER_QUERY").length,
          approved: rows.filter((r) => r.newKmlStatus === "APPROVED").length,
        });
      },
      refetchOnMount: true,
      enabled: registrationType === "registeredEntity",
    },
  );

  const { isFetching: isEntityFetching } = useQuery(
    ["projects", queryParams],
    () =>
      getAllRegistration({
        ...queryParams,
        stateIds: queryParams?.projectStateIds
          ? queryParams?.projectStateIds.join(",")
          : undefined,
      }),
    {
      onSuccess: (data) => {
        dispatch(getAllProjectsData(data.data?.rows));
        const rows = data?.data?.rows?.map((row) => ({
          data: row,
          sno: row?.serialNo,
          id: row.id,
          status: row.status,
          entityName: row?.user?.firstName,
          netPlantationArea: row?.netPlantationArea ?? "-",
          registrationNo: row.registrationNo,
          state: row.state,
          district: row.district,
          division: row?.division,
          newKmlStatus: row?.newKmlStatus ?? null,
          allocatedivision:
            row?.projectDetail?.organizationAffiliationDetail?.userDetail
              ?.division,
          range: row?.projectDetail?.landSpecification?.range,
          treeCanopy: row?.treeCannopyDensity ?? "-",
          landArea: row?.projectDetail?.landArea,
          areaUnit: `${row.totalArea}`,
          demandNote: formatINR(row.demandNote) ?? "-",
          estimateAmount: row.demandNote
            ? formatINR(
                +`${Number(row?.demandNote)}` + +`${Number(row?.icfreCharges)}`,
              )
            : "-",
          totalAmountRecived: formatINR(row.amountPaid) ?? "-",
          ecoRestorationCostRecived: formatINR(row.ecoRestorationCost) ?? "-",
          adminChargesRecived: formatINR(row.adminCharges) ?? "-",
          remark: row.remark,
          created_at: dateAndTimeStamp(row?.created_at, "IST"),
          updated_at: dateAndTimeStamp(row.updated_at, "IST"),
        }));

        if (queryParams.download) {
          interface ExportItem {
            "S.NO"?: any;
            EPID?: any;
            "Registration No"?: any;
            "Entity Name"?: any;
            State?: any;
            District?: any;
            Division?: any;
            "Allocate Division"?: any;
            Range?: any;
            "Total Area"?: any;
            "Area Selected"?: any;
            Remark?: any;
            "Created At"?: any;
            "Updated At"?: any;
            Status?: any;
            "Final Area"?: any;
            "Tree Canopy Density"?: any;
            Eco_Restoration_Cost?: any;
            DemandNote_Amount?: any;
            "Total Amount Received"?: any;
            "Eco-Restoration Cost Received"?: any;
            "Admin Charges Received"?: any;
          }

          const exportData: ExportItem[] = rows.map((item) => {
            const status = statusMessages2[item?.status] ?? item?.status;
            const data: ExportItem = {
              "S.NO": item?.sno,
              EPID: item?.id,
              "Registration No": item?.registrationNo,
              "Entity Name": item?.entityName,
              State: item?.state,
              District: item?.district,
              Division: item?.division,
              "Allocate Division": item?.allocatedivision,
              Range: item?.range,
              "Tree Canopy Density": item?.treeCanopy,
              "Total Area": item?.landArea,
              "Area Selected": item?.areaUnit,
              "Final Area": item?.netPlantationArea,
              "Created At": item?.created_at,
              "Updated At": item?.updated_at,
              "Total Amount Received": item?.totalAmountRecived,
              "Eco-Restoration Cost Received": item.ecoRestorationCostRecived,
              "Admin Charges Received": item.adminChargesRecived,
            };

            if (status === "UNDER_QUERY" || status === "VERIFICATION_PENDING") {
              // Exclude eco_restoration_cost and demandNote_Amount
              data.Eco_Restoration_Cost = "-";
              data.DemandNote_Amount = "-";
            } else {
              // Include eco_restoration_cost and demandNote_Amount
              data.Eco_Restoration_Cost = item?.demandNote ?? "-";
              data.DemandNote_Amount = item?.estimateAmount ?? "-";
            }

            data.Status = ExportStatusMessages2[item?.status] ?? item?.status;
            data.Remark = item?.remark;

            return data;
          });

          downloadSheet(exportData, generateExportFilename("Cost-Estimate"));
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          setProjectData({
            count: data?.data.count,
            rows,
          });
        }
      },
      refetchOnMount: true,
      enabled: registrationType === "registeredEntity",
    },
  );

  const { isFetching: isPlantationFetching } = useQuery(
    ["plantationProjects", queryParams],
    () => {
      const isOldTab = queryParams.status === "OLD_APPROVED";
      const apiStatus =
        queryParams.status === "READY_FOR_SELECTION"
          ? "APPROVED"
          : isOldTab
            ? undefined // no project-status filter — return all flagged legacy lands
            : queryParams.status;
      return getAllProjects({
        ...queryParams,
        status: apiStatus,
        // Old Approved tab → only legacy lands; Approved tab → exclude legacy
        // (they live under Old Approved instead); every other tab → no filter.
        isLegacy: isOldTab
          ? true
          : queryParams.status === "APPROVED"
            ? false
            : undefined,
        projectStateIds: queryParams?.projectStateIds
          ? queryParams?.projectStateIds.join(",")
          : undefined,
      });
    },
    {
      onSuccess: (res: AxiosResponse) => {
        const cutoffDate = getBatchCutoffDate();
        let rows = res?.data?.rows.map((row) => {
          let status = registartionStatus[row.status] || row.status;

          // Override APPROVED → READY_FOR_SELECTION if updated_at is in current batch window
          // (skip for the Old Approved tab so legacy lands keep their real status)
          if (
            queryParams.status !== "OLD_APPROVED" &&
            row.status === "APPROVED" &&
            new Date(row.updated_at) >= cutoffDate
          ) {
            status = "READY_FOR_SELECTION";
          }

          return {
            data: row,
            sno: row?.serialNo,
            id: row.id,
            registrationNo: row.registrationNo,
            state: row?.address?.stateDetail?.name,
            district: row?.address?.districtDetail?.name,
            division: row?.landSpecification?.division ?? "-",
            range: row?.landSpecification?.range ?? "-",
            treeCanopy: row?.treeCannopyDensity ?? "-",
            allocatedivision:
              row?.organizationAffiliationDetail?.userDetail?.division,
            areaUnit: `${(+row.landArea).toFixed(2)}`,
            created_at: dateAndTimeStamp(row?.created_at, "IST"),
            updated_at: dateAndTimeStamp(row?.updated_at, "IST"),
            status: status,
            remark: row.remarks,
          };
        });

        // Client-side filter for READY_FOR_SELECTION tab
        if (queryParams.status === "READY_FOR_SELECTION") {
          rows = rows.filter((r) => r.status === "READY_FOR_SELECTION");
        }

        if (queryParams.download) {
          const exportData = rows.map((item) => {
            return {
              "S.No": item?.sno,
              "Registration No": item?.registrationNo,
              State: item?.state,
              Status: item?.status,
              District: item?.district,
              Division: item?.division,
              Range: item?.range,
              "Tree Canopy Density": item?.treeCanopy,
              "Total Area (Ha)": item?.areaUnit,
              "Created At": item?.created_at,
              "Updated At": item?.updated_at,
              Remark: item?.remark,
            };
          });
          downloadSheet(exportData, generateExportFilename("registered-lands"));
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          setPlantationBlockData({
            count:
              queryParams.status === "READY_FOR_SELECTION"
                ? rows.length
                : res.data.count,
            rows,
          });
        }
      },
      refetchOnMount: true,
      enabled: registrationType !== "registeredEntity",
    },
  );

  const { entityInfo } = useLandOwnershipEntityOptions({
    status: queryParams.status,
    stateIds: queryParams.projectStateIds?.join(","),
    searchText: queryParams.searchText,
  }, registrationType === "registeredEntity");

  const columns = useMemo(() => {
    return tableColumns
      .map((item) => ({
        ...item,
        renderCell: Mapping[item.renderCell],
      }))
      .filter((item) => {
        return (
          item.type.includes(registrationType) &&
          !!!item.tab.includes(activeTab)
        );
      });
  }, [activeTab]);

  const entitiesColumns = useMemo(
    () =>
      tableColumns
        .map((item) => ({
          ...item,
          renderCell: Mapping[item.renderCell],
        }))
        .filter((item) => {
          return item.type.includes(registrationType);
        })
        .filter((item) => {
          return !!!item.tab.includes(entityActiveTab);
        }),
    [registrationType, entityActiveTab],
  );

  const handlePagination = (page: any) => {
    dispatch(refetchAllProjects(true));
    setQueryParams({
      ...queryParams,
      offset: queryParams.limit * page,
    });
  };

  const handleSort = (sortByField) => {
    setQueryParams({
      ...queryParams,
      sortBy: `${sortByField[0]?.field}:${sortByField[0]?.sort.toUpperCase()}`,
    });
  };

  const exportToExcel = () => {
    setQueryParams({
      ...queryParams,
      download: true,
    });
  };

  const downloadAllKmls = async () => {
    const apiStatus =
      queryParams.status === "READY_FOR_SELECTION"
        ? "APPROVED"
        : queryParams.status;

    const { data } = await getAllProjects({
      ...queryParams,
      status: apiStatus,
      download: true,
      projectStateIds: queryParams?.projectStateIds
        ? queryParams.projectStateIds.join(",")
        : undefined,
    });

    const rows = data?.rows ?? [];

    for (const row of rows) {
      const { data: projectData } = await getProjectById(row.id);
      const kmlDoc = (projectData as any)?.documentDetail?.find(
        (doc: any) => doc.type === "LAND_KML_FILE",
      );

      if (kmlDoc?.documentUrl && projectData?.registrationNo) {
        await downloadS3File(
          kmlDoc.documentUrl,
          `${projectData.registrationNo}.kml`,
        );
      }
    }
  };

  const KML_DOWNLOAD_STATUSES = [
    "NODAL_VERIFICATION_PENDING",
    "VERIFICATION_PENDING",
    "NODAL_UNDER_QUERY",
    "UNDER_QUERY",
    "APPROVED",
  ];

  const downloadAllKmlsForStatuses = async () => {
    for (const status of KML_DOWNLOAD_STATUSES) {
      const { data } = await getAllProjects({
        ...queryParams,
        status,
        download: true,
        projectStateIds: queryParams?.projectStateIds
          ? queryParams.projectStateIds.join(",")
          : undefined,
      });

      const rows = data?.rows ?? [];
      // console.log(`[downloadAllKmls] status=${status} rowCount=${rows.length}`);

      for (const row of rows) {
        const { data: projectData } = await getProjectById(row.id);
        const kmlDoc = (projectData as any)?.documentDetail?.find(
          (doc: any) => doc.type === "LAND_KML_FILE",
        );

        if (kmlDoc?.documentUrl && projectData?.registrationNo) {
          // console.log(
          //   `[downloadAllKmls] downloading id=${row.id} registrationNo=${projectData.registrationNo} url=${kmlDoc.documentUrl}`,
          // );
          await downloadS3File(
            kmlDoc.documentUrl,
            `${projectData.registrationNo}.kml`,
          );
        } else {
          // console.log(
          //   `[downloadAllKmls] skipping id=${row.id} registrationNo=${projectData?.registrationNo} hasKmlDoc=${!!kmlDoc} documentUrl=${kmlDoc?.documentUrl}`,
          // );
        }
      }
    }
  };

  const exportToExcel2 = () => {
    setQueryParams({
      ...queryParams,
      download: true,
    });
  };

  const downloadAllRegistrationKmls = async () => {
    const { data } = await getAllRegistration({
      ...queryParams,
      status: entityActiveTab,
      stateIds: queryParams?.projectStateIds
        ? queryParams.projectStateIds.join(",")
        : undefined,
      download: true,
    });

    const rows = data?.rows ?? [];

    for (const row of rows) {
      if (row?.grantedLandKmlId && row?.registrationNo) {
        await downloadS3File(
          row.grantedLandKmlId,
          `${row.registrationNo}.kml`,
        );
      }
    }
  };

  return (
    <DashboardWrapper
      title={
        registrationType === "registeredEntity"
          ? "Status of Cost Estimate"
          : "Eco-Restoration Blocks"
      }
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={!!projectData.rows.length}
    >
      {registrationType === "registeredEntity" ? (
        <>
          {/* KML status summary cards */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              px: "1%",
              background: "white",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            {[
              { label: "Not Uploaded KML", count: kmlCounts.notUploaded, bg: "#EEEEEE", color: "#616161", border: "#BDBDBD" },
              { label: "Uploaded KML",     count: kmlCounts.uploaded,    bg: "#FFF8E1", color: "#F57F17", border: "#FFE082" },
              { label: "KML Under Query",  count: kmlCounts.underQuery,  bg: "#FBE9E7", color: "#C62828", border: "#FFAB91" },
              { label: "Approved KML",     count: kmlCounts.approved,    bg: "#E8F5E9", color: "#2E7D32", border: "#A5D6A7" },
            ].map(({ label, count, bg, color, border }) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 140,
                  px: 2.5,
                  py: 0.5,
                  borderRadius: "10px",
                  background: bg,
                  border: `1px solid ${border}`,
                }}
              >
                <Box sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>
                  {count}
                </Box>
                <Box sx={{ fontSize: 12, fontWeight: 500, color, mt: 0.4 }}>
                  {label}
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "2%",
              padding: "10px 1%",
              background: "white",
            }}
          >
            <SearchBar
              placeHolder="Search by Registration Number"
              defaultValue={persisted?.queryParams?.searchText || ""}
              getText={(e) => {
                setQueryParams((a) => {
                  return { ...queryParams, searchText: `${e}` };
                });
              }}
            />
            <MultiSelect
              options={states}
              value={filterKey.projectStateIds}
              onChange={(e: any) => {
                setFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    projectStateIds: e.length ? e : null,
                  };

                  setQueryParams({
                    ...queryParams,
                    projectStateIds: newState?.projectStateIds,
                  });

                  return newState;
                });
              }}
            />
            <MultiSelect
              options={entityInfo}
              label={"Filter By Entity"}
              clearable
              value={entityfilterKey.entityIds}
              onChange={(e: any) => {
                entitySetFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    entityIds: e.length ? e : null,
                  };

                  setQueryParams({
                    ...queryParams,
                    entityIds: newState?.entityIds,
                    offset: 0,
                  });

                  return newState;
                });
              }}
            />
            <Button onClick={exportToExcel2} text={"Export"} />
            {/* <Button
              onClick={downloadAllRegistrationKmls}
              text={"Download KMLs"}
            /> */}
          </Box>
          <HorizontalTabs
            tabHeaders={registeredEntitiesTabs}
            tabIndex={registeredEntitiesTabs.findIndex(
              (item) => item.value === entityActiveTab,
            )}
            onChange={(e: RegistrationType) => {
              dispatch(refetchAllProjects(true));
              setEntityActiveTab(e);
              navigate(`${path.home}${e ? `?currentTab=${e}` : ""}`);
              setQueryParams({
                ...queryParams,
                status: e,
                offset: 0,
              });
            }}
            components={[<></>, <></>]}
          />
          {isEntityFetching && !projectData.rows.length ? (
            <HomeTableSkeleton showTabs={false} columns={entitiesColumns.length || 9} />
          ) : (
            <RenderTable
              rows={projectData.rows}
              columns={entitiesColumns}
              handleSortModelChange={handleSort}
              hideFooterPagination={false}
              isLoading={isEntityFetching}
              rowCount={projectData?.count / queryParams.limit}
              onPageChange={(page) => handlePagination(page)}
              pageNumber={queryParams.offset / queryParams.limit + 1}
            />
          )}
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "3%",
              padding: "8px 1%",
              background: "white",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <SearchBar
              placeHolder="Search by Registration Number..."
              defaultValue={persisted?.queryParams?.searchText || ""}
              getText={(e) => {
                setQueryParams((a) => {
                  return { ...queryParams, searchText: `${e}` };
                });
              }}
            />
            <SingleSelectFilter
              label="Filter By Status"
              options={registeredLandTabs}
              value={activeTab}
              onChange={(value: filterProjectType) => {
                dispatch(refetchAllProjects(true));
                setActiveTab(value);
                navigate(
                  `${path.registeredLands}${
                    value ? `?currentTab=${value}` : ""
                  }`,
                );
                setQueryParams({
                  ...queryParams,
                  status: value,
                  offset: 0,
                });
              }}
            />
            <MultiSelect
              options={states}
              value={filterKey.projectStateIds}
              onChange={(e: any) => {
                setFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    projectStateIds: e.length ? e : null,
                  };

                  setQueryParams({
                    ...queryParams,
                    projectStateIds: newState?.projectStateIds,
                  });

                  return newState;
                });
              }}
            />
            <Button onClick={exportToExcel} text={"Export"} />
            {/* <Button onClick={downloadAllKmls} text={"Download KMLs eco"} />
            <Button
              onClick={downloadAllKmlsForStatuses}
              text={"Download KMLs all"}
            /> */}
          </Box>
          <HorizontalTabs
            customStyles={{
              tab: {
                whiteSpace: "normal",
                lineHeight: "1.2",
                fontSize: "18px",
                textAlign: "left",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                width: "100%",
                minWidth: "110px",
                maxWidth: "140px",
              },
            }}
            tabHeaders={registeredLandTabs}
            tabIndex={registeredLandTabs.findIndex(
              (item) => item.value === activeTab,
            )}
            onChange={(e: filterProjectType) => {
              dispatch(refetchAllProjects(true));
              setActiveTab(e);
              navigate(`${path.registeredLands}${e ? `?currentTab=${e}` : ""}`);
              setQueryParams({
                ...queryParams,
                status: e,
                offset: 0,
              });
            }}
            components={[<></>, <></>]}
          />

          {isPlantationFetching && !plantationBlockData.rows.length ? (
            <HomeTableSkeleton showTabs={false} columns={columns.length || 9} />
          ) : (
            <RenderTable
              rows={plantationBlockData.rows}
              columns={columns}
              handleSortModelChange={handleSort}
              hideFooterPagination={false}
              isLoading={isPlantationFetching}
              rowCount={plantationBlockData?.count / queryParams.limit}
              onPageChange={(page) => handlePagination(page)}
              pageNumber={queryParams.offset / queryParams.limit + 1}
            />
          )}
        </>
      )}
    </DashboardWrapper>
  );
};

export default Home;
